/**
 * Logged fetch
 *
 * `fetch()` wrapper that emits exactly one summary line per call through the
 * logger pipeline: method, host, path, status, duration_ms, request_id (from
 * the active orchestrator request, if any) and, on failure, the error with
 * stack plus a failure classification.
 *
 * Never logged: request/response bodies, headers (Authorization included),
 * and the query string / fragment of the URL (presigned URLs carry
 * signatures there). Pipeline redaction stays as defence in depth.
 */

import { logger, LogCategory } from '@/utils/logger';
import type { CategoryLogger } from '@/utils/logger.types';
import { toError } from '@/utils/to-error';
import { getActiveRequestId } from '@/utils/request-context';

export enum FetchFailureKind {
  /** Our own timeout fired */
  TIMEOUT = 'timeout',
  /** Aborted by something other than our timeout */
  ABORT = 'abort',
  /** fetch() rejected (offline, DNS, CORS, ...) */
  NETWORK = 'network',
  /** Server answered with a non-2xx status */
  HTTP_STATUS = 'http_status',
}

const MAX_PATH_LENGTH = 200;

/** Call-site labels for the `target` log field. */
export enum FetchTarget {
  API = 'api',
  EXTERNAL_JSON = 'external_json',
  S3_UPLOAD = 's3_upload',
  AVATAR = 'avatar',
  CONNECTIVITY_PROBE = 'connectivity_probe',
}

/** Synthetic error for 5xx lines: a distinct name groups them sensibly. */
class HttpStatusError extends Error {
  constructor(status: number, path: string) {
    super(`HTTP ${status} from ${path}`);
    this.name = 'HttpStatusError';
  }
}

/** Logging must never turn a good response or the real error into another failure. */
function safely(fn: () => void): void {
  try {
    fn();
  } catch {
    // Intentionally ignored.
  }
}

/** Query-free, bounded `host/path` form of any URL, safe to log. */
export function describeUrlForLog(url: string): string {
  const { host, path } = describeUrl(url);
  return `${host ?? ''}${path}`;
}

/** Response header the server may expose to correlate with its own logs. */
const SERVER_REQUEST_ID_HEADER = 'X-Request-ID';

export interface LoggedFetchOptions {
  category?: LogCategory;
  /** Abort the request after this many ms (classified as `timeout`). */
  timeoutMs?: number;
  /**
   * Attach the active orchestrator request id (default false: opt-in).
   * Only calls made on behalf of an orchestrator action (HttpClient) pass
   * true, so other calls are never attributed to an unrelated action.
   */
  tagRequest?: boolean;
  /** Short call-site label, e.g. 'api', 's3_upload'. */
  target?: FetchTarget;
}

/**
 * host + pathname only. Query string and fragment are dropped entirely
 * (their values may be secrets); `has_query` records that one existed.
 */
function describeUrl(url: string): {
  host?: string;
  path: string;
  has_query: boolean;
} {
  // data:/blob: URLs embed payloads or opaque ids: log only the scheme.
  const opaque = /^(data|blob):/i.exec(url);
  if (opaque) {
    return { path: `${opaque[1].toLowerCase()}:[omitted]`, has_query: false };
  }
  const queryIndex = url.search(/[?#]/);
  const hasQuery = queryIndex >= 0;
  const withoutQuery = hasQuery ? url.slice(0, queryIndex) : url;
  const match = /^[a-z][a-z0-9+.-]*:\/\/([^/]*)(\/.*)?$/i.exec(withoutQuery);
  if (match) {
    const authority = match[1];
    const host = authority.slice(authority.lastIndexOf('@') + 1);
    return {
      host,
      path: (match[2] ?? '/').slice(0, MAX_PATH_LENGTH),
      has_query: hasQuery,
    };
  }
  return { path: withoutQuery.slice(0, MAX_PATH_LENGTH), has_query: hasQuery };
}

function isAbortError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { name?: string }).name === 'AbortError'
  );
}

function readServerRequestId(response: Response): string | undefined {
  try {
    return response.headers?.get(SERVER_REQUEST_ID_HEADER) ?? undefined;
  } catch {
    return undefined;
  }
}

export async function loggedFetch(
  url: string,
  init: RequestInit = {},
  options: LoggedFetchOptions = {}
): Promise<Response> {
  const {
    category = LogCategory.API,
    timeoutMs,
    target,
    tagRequest = false,
  } = options;
  const method = (init.method ?? 'GET').toUpperCase();
  const startTime = Date.now();
  const requestId = tagRequest ? getActiveRequestId() : undefined;
  const base = requestId ? logger.withRequest(requestId) : logger;
  const logByCategory: Partial<Record<LogCategory, CategoryLogger>> = {
    [LogCategory.NETWORK]: base.network,
    [LogCategory.STORAGE]: base.storage,
    [LogCategory.API]: base.api,
  };
  const log = logByCategory[category] ?? base.api;
  const summary = { method, ...describeUrl(url), ...(target && { target }) };

  let timedOut = false;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  let signal = init.signal ?? undefined;
  let onOuterAbort: (() => void) | undefined;
  if (timeoutMs !== undefined) {
    const controller = new AbortController();
    const outer = init.signal;
    if (outer) {
      if (outer.aborted) controller.abort();
      else {
        onOuterAbort = () => controller.abort();
        outer.addEventListener('abort', onOuterAbort, { once: true });
      }
    }
    timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);
    signal = controller.signal;
  }

  let response: Response;
  try {
    response = await fetch(url, { ...init, signal });
  } catch (error) {
    const failure_kind = timedOut
      ? FetchFailureKind.TIMEOUT
      : isAbortError(error)
        ? FetchFailureKind.ABORT
        : FetchFailureKind.NETWORK;
    safely(() => {
      const failure = {
        ...summary,
        failure_kind,
        duration_ms: Date.now() - startTime,
      };
      if (failure_kind === FetchFailureKind.ABORT) {
        // Expected (unmount, navigation, caller cancel): not an error.
        log.warn('HTTP request aborted', failure);
      } else if (target === FetchTarget.CONNECTIVITY_PROBE) {
        // Offline is the expected outcome of a probe: don't flood error logs.
        log.warn('Connectivity probe failed', failure);
      } else {
        log.error('HTTP request failed', toError(error), failure);
      }
    });
    throw error;
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
    if (onOuterAbort) init.signal?.removeEventListener('abort', onOuterAbort);
  }

  safely(() => {
    const server_request_id = readServerRequestId(response);
    const context = {
      ...summary,
      status: response.status,
      duration_ms: Date.now() - startTime,
      ...(server_request_id && { server_request_id }),
    };
    // type 'opaque' (no-cors) hides the status: not a failure.
    if (response.ok || response.type === 'opaque') {
      log.debug('HTTP request completed', context);
    } else if (response.status >= 500) {
      log.error(
        'HTTP request failed',
        new HttpStatusError(response.status, summary.path),
        { ...context, failure_kind: FetchFailureKind.HTTP_STATUS }
      );
    } else {
      log.warn('HTTP request failed', {
        ...context,
        failure_kind: FetchFailureKind.HTTP_STATUS,
      });
    }
  });
  return response;
}
